// src/hooks/useOrderNotifications.js
import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import { getRestaurantDetails } from '../services/firebaseService'; // To get currency
import { getCurrencySymbol } from '../config/currencies';

const notificationSound = new Audio('/notification.mp3'); // Ensure notification.mp3 is in public folder

function useOrderNotifications(restaurantId) {
  const [error, setError] = useState('');
  // currencySymbol state will be managed internally by the hook
  const [currencySymbol, setCurrencySymbol] = useState('$'); 

  // Request notification permission with feature detection
  useEffect(() => {
    // Check if Notification API is supported (not available on iOS Safari in some cases)
    if ('Notification' in window && typeof Notification.requestPermission === 'function') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          console.log('Notification permission granted for hook.');
        } else {
          console.log('Notification permission denied or dismissed for hook.');
        }
      });
    } else {
      console.log('Notification API not supported on this device/browser.');
    }
  }, []);

  const playNotificationSound = () => {
    notificationSound.play().catch(err => console.error("Error playing sound from hook:", err));
  };

  const showBrowserNotification = useCallback((order, currentCurrencySymbol) => {
    // Check if Notification API is supported and permission is granted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('New Order Received!', {
        body: `Order #${order.orderNumber || order.id} from ${order.customerName} for table ${order.tableNumber}. Total: ${currentCurrencySymbol}${(order.totalAmount || 0).toFixed(2)}`,
        icon: '/logo192.png' // Ensure logo192.png is in public folder
      });
      playNotificationSound();
    } else {
      // Fallback: Just play sound and log to console when notifications aren't available
      console.log('New Order Received!', `Order #${order.orderNumber || order.id} from ${order.customerName} for table ${order.tableNumber}. Total: ${currentCurrencySymbol}${(order.totalAmount || 0).toFixed(2)}`);
      playNotificationSound();
    }
  }, []);

  useEffect(() => {
    if (!restaurantId) {
      setError('Restaurant ID is not provided to useOrderNotifications hook.');
      return;
    }

    // Fetch restaurant details to get currency
    const fetchRestaurantCurrency = async () => {
      try {
        const details = await getRestaurantDetails(restaurantId);
        if (details && details.currency) {
          setCurrencySymbol(getCurrencySymbol(details.currency));
        } else {
          console.warn('Could not fetch restaurant currency for notifications, using default.');
        }
      } catch (err) {
        console.error('Error fetching restaurant currency for notifications:', err);
        setError('Failed to load restaurant currency for notifications.');
      }
    };

    fetchRestaurantCurrency();

    const ordersRef = collection(db, 'restaurants', restaurantId, 'orders');
    // We only care about new orders for notifications, so we can filter by status 'new'
    // and listen for changes. Or listen to all and filter client-side.
    // For simplicity and to catch orders as they become 'new', let's listen to recent orders.
    const q = query(ordersRef, orderBy('createdAt', 'desc'));

    // Keep track of notified orders to avoid duplicate notifications for the same order
    // This simple set might not be perfectly robust across page reloads if not persisted,
    // but good for a session.
    const notifiedOrderIds = new Set();
    let initialLoadComplete = false;

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const newOrders = [];
      querySnapshot.forEach((doc) => {
        const order = { id: doc.id, ...doc.data() };
        if (order.status === 'new') {
          newOrders.push(order);
        }
      });

      if (!initialLoadComplete) {
        // On initial load, mark all current new orders as 'seen' to avoid old notifications
        newOrders.forEach(order => notifiedOrderIds.add(order.id));
        initialLoadComplete = true;
        return;
      }

      newOrders.forEach(order => {
        if (!notifiedOrderIds.has(order.id)) {
          // Check if the order was created recently (e.g., within the last 2 minutes)
          // to avoid old 'new' orders triggering notifications on reconnects or if status changes back to new.
          const orderTimestamp = order.createdAt instanceof Timestamp ? order.createdAt.toMillis() : new Date(order.createdAt).getTime();
          const twoMinutesAgo = Date.now() - 2 * 60 * 1000;
          
          if (orderTimestamp > twoMinutesAgo) {
            showBrowserNotification(order, currencySymbol); // Pass current currencySymbol
            notifiedOrderIds.add(order.id);
            // Update document title for visual cue if this hook is used on the active page
            // This might be better handled by the component using the hook if specific title formats are needed.
            // For now, let's keep it simple or allow the component to handle it.
            // document.title = `(${newOrders.filter(o => !notifiedOrderIds.has(o.id)).length}) New Order!`;
          }
        }
      });

    }, (err) => {
      console.error('Error in useOrderNotifications snapshot listener:', err);
      setError('Failed to listen for new orders.');
    });

    return () => {
      unsubscribe();
      // document.title = 'CheckMenus'; // Reset title if hook was managing it
    };
  }, [restaurantId, showBrowserNotification, currencySymbol]); // Add currencySymbol to dependencies

  return { notificationError: error }; // Return error state if needed by component
}

export default useOrderNotifications;